import {
  type RequestQuery,
  type ResponseData,
  useApiQuery,
} from '@vrplatform/data';
import { hasValue, sum } from '@vrplatform/utils';
import { useMemo } from 'react';
import { useProfitAndLossTableFilter } from './useProfitAndLossTableFilter';

export const STORYBOOK_PROFIT_AND_LOSS_TABLE_QUERY_KEY =
  'profit-and-loss-table';

export type ProfitAndLossRow = {
  id: string;
  title: string;
  values: RowValues;
  computed?: RowValues;
  subRows: ProfitAndLossRow[];
  columns: { id: string; title: string }[];
  type: 'group' | 'classification' | 'account' | 'category';
  total: number;
  totalPercentage?: number;
};

type PickByKey<T, K extends PropertyKey> = T extends Record<K, any> ? T : never;

type NetIncome = NonNullable<
  ResponseData<'get:/reports/profit-and-loss'>['netIncome']
>;

type ClassificationRow = NonNullable<NetIncome['groups']>[0];
type CategoriesRow = PickByKey<
  Required<ClassificationRow>,
  'categories'
>['categories'][0];
type AccountsRow = CategoriesRow['accounts'][0];
type AnyRowType = (
  | ClassificationRow
  | NetIncome
  | CategoriesRow
  | AccountsRow
) & {
  type?: ProfitAndLossRow['type'];
};

type RowValues = {
  [columnId: string]: {
    title: string;
    total: number;
  };
};

export type ProfitAndLossTableData = {
  columns: {
    id: string;
    title: string;
  }[];
  rows: ProfitAndLossRow[];
};

const filterNonZeroRows = (rows: ProfitAndLossRow[]): ProfitAndLossRow[] => {
  return rows
    .map<ProfitAndLossRow | undefined>((row) => {
      const columnValues = Object.values(row.values);
      const subRows = filterNonZeroRows(row.subRows);

      if (
        subRows.length === 0 &&
        columnValues.every((value) => value.total === 0)
      ) {
        return undefined;
      }

      return {
        ...row,
        subRows,
      };
    })
    .filter(hasValue);
};

export const getProfitAndLossQueryFilterParams = (
  args: Omit<
    ReturnType<typeof useProfitAndLossTableFilter>['filter'],
    'date' | 'view'
  >
): RequestQuery<'get:/reports/profit-and-loss'> => {
  return {
    startDate: args.startDate,
    endDate: args.endDate,
    listingIds: args.listingIds?.join(','),
    ledger: args.ledger ?? undefined,
    party: args.party ?? undefined,
    view: args.type ?? undefined,
  };
};

const formatValues = (
  v: {
    id: string;
    title: string;
    total: number;
    percentage?: number;
  }[],
  key: 'total' | 'percentage' = 'total'
) =>
  v.reduce(
    (acc, curr) => {
      if (!acc[curr.id]) {
        acc[curr.id] = {
          title: curr.title,
          total: curr[key] ?? 0,
        };
      } else {
        acc[curr.id].total += curr.total;
      }

      return acc;
    },
    {} as Record<
      string,
      {
        title: string;
        total: number;
      }
    >
  );

const formatAmbigiousRow = (row: AnyRowType): ProfitAndLossRow => {
  const classificationSubRows = 'groups' in row ? (row.groups ?? []) : [];
  const categorySubRows = 'categories' in row ? (row.categories ?? []) : [];

  const valueSubRows =
    'accounts' in row
      ? row.accounts.map<ClassificationRow>((account) => ({
          id: account.id,
          title: account.title,
          total: account.total,
          values: account.values.map((value) => ({
            id: value.id,
            title: value.title,
            total: value.total,
          })),
          type: 'account',
        }))
      : [];

  const subRows = (
    classificationSubRows.length
      ? classificationSubRows
      : categorySubRows.length
        ? categorySubRows
        : valueSubRows
  ).map(formatAmbigiousRow);

  const columns =
    'values' in row
      ? (row.values?.map((v) => ({
          id: v.id,
          title: v.title,
        })) ?? [])
      : (subRows?.[0]?.columns ?? []);

  const values = formatValues(
    'values' in row
      ? (row.values ?? [])
      : columns.map((c) => ({
          id: c.id,
          title: c.title,
          total: sum(subRows.map((s) => s.values[c.id]?.total ?? 0)),
        }))
  );

  const computed =
    'computedValues' in row && row.computedValues
      ? formatValues(row.computedValues, 'percentage')
      : undefined;

  const isClassification = 'categories' in row;
  const isCategory = 'accounts' in row;

  const totalPercentage =
    'totalPercentage' in row ? row.totalPercentage : undefined;

  return {
    id: row.id,
    title: row.title,
    total: row.total,
    values,
    totalPercentage,
    subRows,
    columns,
    computed,
    type:
      row.type ??
      (isClassification ? 'classification' : isCategory ? 'category' : 'group'),
  };
};

export const useProfitAndLossTableQuery = () => {
  const { filter } = useProfitAndLossTableFilter();

  const hideNonZeroRows = filter.view?.includes('non-zero-rows');

  const query = useApiQuery(
    async (api, signal, args): Promise<ProfitAndLossTableData> => {
      const netIncome = await api
        .GET('/reports/profit-and-loss', {
          params: {
            query: getProfitAndLossQueryFilterParams(args),
          },
          signal,
        })
        .then((res) => {
          if (res.error) throw res.error;

          return res.data.netIncome;
        });

      if (netIncome) {
        const rows = [netIncome].map(formatAmbigiousRow);
        return {
          columns: rows?.[0]?.columns ?? [],
          rows,
        };
      }

      return {
        columns: [],
        rows: [],
      };
    },
    {
      variables: {
        startDate: filter.startDate,
        endDate: filter.endDate,
        type: filter.type,
        listingIds: filter.listingIds,
        ledger: filter.ledger,
        party: filter.party,
      },
      storybookQueryKey: STORYBOOK_PROFIT_AND_LOSS_TABLE_QUERY_KEY,
      queryGroup: 'reports',
      uniqueQueryKey: 'profit-and-loss-report',
      queryKeys: ['journalEntries', 'reports'],
    }
  );

  const data = useMemo(() => {
    if (hideNonZeroRows && query.data) {
      return {
        ...query.data,
        rows: filterNonZeroRows(query.data.rows),
      };
    }

    return query.data;
  }, [query.data, hideNonZeroRows]);

  return {
    ...query,
    data,
  };
};
